/**
 * Product Gallery V2 — spotlight + thumbnail strip runtime.
 *
 * ONE Embla track drives every viewport. The markup renders a single slide per
 * asset and CSS reflows the stage across the 768px breakpoint (thumb rail beside
 * the spotlight above it, dots or a thumb row beneath it below) — so there is no
 * second, hidden copy of the gallery to keep in sync, and no re-homing of the
 * carousel on resize. A breakpoint change only asks Embla to re-measure.
 * Lightbox: white scrollable dialog with all filtered images stacked.
 * Variant change: URL ?variant= observation.
 *
 * Analytics events:
 *   product_gallery:image_view_elem_clicked       — navigate to new index
 *   product_gallery:lightbox_open_elem_clicked    — lightbox opened
 *   product_gallery:lightbox_close_elem_clicked   — lightbox closed (with dwell_ms)
 *   product_gallery:video_play_elem_clicked       — video play
 *   product_gallery:video_pause_elem_clicked      — video pause
 *   product_gallery:filter_apply_elem_clicked     — filter applied
 */
;(() => {
  if (window.__sai_emblag01_initialized__) return
  window.__sai_emblag01_initialized__ = true

  const SNIPPET_ID = 'emblag01'
  const TAG = 'sai-emblag01'
  const FEATURE_SLUG = 'product_gallery'
  const FILTER_STORAGE_KEY = `spectrum:${FEATURE_SLUG}:filters`
  // Extra px kept between the active dot and the rail edge when scrolling it
  // back into view, so it never sits flush against the clipped boundary.
  const DOT_SCROLL_PAD = 12
  // The desktop breakpoint: at/above this the thumb rail is a vertical column and
  // the spotlight sits beside it; below it the layout is a single mobile column.
  const DESKTOP_MQ = '(min-width: 768px)'
  // Windowed ("paged") dot pagination: a fixed 5-dot window that keeps the
  // active dot centred. Deterministic and DOM-free so it is unit-tested directly.
  //   count = total dots, active = current index.
  //   Returns one label per index: 'pill' (active) | 'normal' | 'small' | 'hidden'.
  // Sizes come from the Figma spec: active is a 21×6 pill, the in-window dots are
  // 6px, and the outermost dots on any side that still has hidden slides beyond
  // the window shrink to 4px to signal "more". When both sides overflow, one dot
  // shrinks on each edge; when only one side does, its two outer dots shrink — so
  // the window always shows exactly two small dots once the strip overflows.
  const DOT_WINDOW = 5
  function computeDotWindow(count, active, windowSize = DOT_WINDOW) {
    const labels = new Array(count).fill('hidden')
    if (count <= 0) return labels
    const a = Math.max(0, Math.min(active, count - 1))
    if (count <= windowSize) {
      for (let i = 0; i < count; i++) labels[i] = i === a ? 'pill' : 'normal'
      return labels
    }
    const start = Math.max(0, Math.min(a - Math.floor(windowSize / 2), count - windowSize))
    const end = start + windowSize - 1
    const leftOverflow = start > 0
    const rightOverflow = end < count - 1
    const smallLeft = leftOverflow ? (rightOverflow ? 1 : 2) : 0
    const smallRight = rightOverflow ? (leftOverflow ? 1 : 2) : 0
    for (let i = start; i <= end; i++) {
      const p = i - start
      if (i === a) labels[i] = 'pill'
      else if (p < smallLeft || p > windowSize - 1 - smallRight) labels[i] = 'small'
      else labels[i] = 'normal'
    }
    return labels
  }
  // Variant/filter-swap crossfade. REMOVE_MS must outlast the CSS opacity
  // transition on .sai-emblag01__xfade or the overlay is torn out mid-fade;
  // LOAD_TIMEOUT_MS is the failsafe for an incoming image that fires neither load
  // nor error, and is deliberately long so it can't cut a slow fetch short.
  const XFADE_REMOVE_MS = 420
  const XFADE_LOAD_TIMEOUT_MS = 3000
  // Gallery source priority within a scope, highest first: an authored
  // `custom-gallery` slot outranks the auto-synced `gallery` slot. resolveSlot
  // walks variant scope before product scope, so the full order is
  // variant custom → variant gallery → product custom → product gallery. Kept
  // byte-identical in intent to the SSR slot resolution in the .liquid so the
  // client re-resolves the same slot the server rendered.
  const SLOT_SLUGS = ['custom-gallery', 'gallery']
  // Mirrors the `sizes` attribute the Liquid bakes onto SSR slides, so a
  // client-rebuilt slide picks the same srcset candidate as the server-rendered
  // one. The breakpoint is the stage's: two-column (spotlight ≈ half the
  // gallery) from 768px, full-bleed below it.
  const SPOTLIGHT_SIZES = '(min-width: 768px) 50vw, 100vw'

  // ── Shared carousel engine loader ──
  // Embla is NOT inlined here. It ships once per theme as
  // assets/spectrum-carousel-engine.js (assigns window.__spectrumCarousel = the
  // Embla factory); _sai-artifacts.liquid parks its URL on
  // window.__spectrumCarouselEngineUrl. Every carousel snippet on the page shares
  // ONE download + parse via the window-memoized promise below. If the engine is
  // absent (old theme, blocked asset), the gallery degrades to the static SSR
  // image — same fallback as a measurement-less environment.
  function ensureCarouselEngine() {
    const w = typeof window !== 'undefined' ? window : null
    if (!w) return Promise.resolve(null)
    if (typeof w.__spectrumCarousel === 'function') return Promise.resolve(w.__spectrumCarousel)
    const url = w.__spectrumCarouselEngineUrl
    if (!url) return Promise.resolve(null)
    if (!w.__spectrumCarouselEnginePromise) {
      w.__spectrumCarouselEnginePromise = new Promise((resolve) => {
        const done = () =>
          resolve(typeof w.__spectrumCarousel === 'function' ? w.__spectrumCarousel : null)
        const existing = document.querySelector('script[data-spectrum-carousel-engine-src]')
        if (existing) {
          existing.addEventListener('load', done)
          existing.addEventListener('error', () => resolve(null))
          return
        }
        const s = document.createElement('script')
        s.src = url
        s.async = true
        s.setAttribute('data-spectrum-carousel-engine-src', '')
        s.onload = done
        s.onerror = () => resolve(null)
        document.head.appendChild(s)
      })
    }
    return w.__spectrumCarouselEnginePromise
  }




  function readSnippetPool(node) {
    for (let i = 0; i < node.children.length; i++) {
      const child = node.children[i]
      if (child.tagName === 'SCRIPT' && child.hasAttribute('data-spectrum-snippet-pool')) {
        const text = child.textContent
        if (!text) return {}
        try {
          return JSON.parse(text)
        } catch (_) {
          return {}
        }
      }
    }
    return {}
  }

  function noopTrack() {}

  function safeTrack(fn) {
    return (...args) => {
      try {
        fn(...args)
      } catch (_) {
        /* analytics must never break the gallery */
      }
    }
  }

  function resolveSlot(pool, variantId) {
    const vs = pool.variantSlots || {}
    const ps = pool.productSlots || {}
    const vid = variantId != null ? String(variantId) : null
    const vSlots = vid ? vs[vid] : null
    // Variant scope first (only populated when enable_variant_gallery is on — the
    // pool emission gates it, so variant tiers self-skip when the feature is off),
    // then product scope; within each, custom-gallery outranks gallery.
    if (vSlots) {
      for (let i = 0; i < SLOT_SLUGS.length; i++) {
        if (vSlots[SLOT_SLUGS[i]]) return vSlots[SLOT_SLUGS[i]]
      }
    }
    for (let i = 0; i < SLOT_SLUGS.length; i++) {
      if (ps[SLOT_SLUGS[i]]) return ps[SLOT_SLUGS[i]]
    }
    return null
  }

  function resolveSlotVariant(slot) {
    if (!slot?.variants || !slot.variants.length) return null
    // Personalisation: return the first targeted variant whose targeting tree
    // matches the live shopper (author priority order), else the untargeted
    // default. Targeting is evaluated with the storefront SDK engine
    // (window.__spectrumAi.targeting) — the same one that resolves experience
    // and snippet-instance targeting. session.utm_source / cart.* are only
    // knowable client-side, so this runs here, never in Liquid; the default is
    // SSR-rendered and swapped once the engine has resolved.
    const engine = window.__spectrumAi && window.__spectrumAi.targeting
    let fallback = null
    for (const v of slot.variants) {
      if (!v.targeting) {
        if (!fallback) fallback = v
        continue
      }
      if (engine && typeof engine.evaluate === 'function') {
        try {
          const result = engine.evaluate(v.targeting)
          const matched = Array.isArray(result) ? result[0] : result
          if (matched) return v
        } catch (_err) {
          // A malformed tree or engine fault must never blank the gallery —
          // skip this variant and fall through to the default.
        }
      }
    }
    return fallback || slot.variants[0] || null
  }

  function resolveAssets(pool, handles) {
    const assets = pool.assets || {}
    const resolved = []
    for (const handle of handles) {
      const asset = assets[handle]
      if (asset?.url) {
        resolved.push(Object.assign({}, asset, { handle }))
      }
    }
    return resolved
  }

  /**
   * Build a map of dimension -> entries from the filterTags declaration on a slot.
   * Handles both old format (string[]) and new format ({value, displayConfig}[]).
   * Returns { dim: [{ value, displayConfig }, ...] } with only dims that have 2+ values.
   */
  function buildFilterTags(filterTags) {
    if (!filterTags || typeof filterTags !== 'object') return {}
    const result = {}
    for (const [dim, values] of Object.entries(filterTags)) {
      if (Array.isArray(values) && values.length >= 1) {
        result[dim] = values.map((v) =>
          typeof v === 'string' ? { value: v, displayConfig: null } : v,
        )
      }
    }
    return result
  }

  /**
   * Filter a list of resolved assets by a set of active filters.
   * Returns a new array containing only assets whose tags match all active filter dimensions.
   */
  function filterAssets(assets, activeFilters) {
    if (!activeFilters || Object.keys(activeFilters).length === 0) return assets.slice()
    return assets.filter((asset) => {
      const tags = asset.tags || {}
      return Object.entries(activeFilters).every(([dim, val]) => tags[dim] === val)
    })
  }

  // Stable signature of a resolved asset set (type + handle, in order). Used to
  // detect a redundant _rebuildGallery re-entry so it doesn't rebuild slides that
  // already render the same assets.
  function assetsKey(assets) {
    return (assets || []).map((a) => `${a.type}:${a.handle}`).join('|')
  }

  // ── DOM helpers ──

  // SVG icon strings for video controls
  const ICON_PLAY =
    '<svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>'
  const ICON_PAUSE =
    '<svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/></svg>'
  const ICON_MUTED =
    '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><line x1="23" y1="9" x2="17" y2="15"/><line x1="17" y1="9" x2="23" y2="15"/></svg>'
  const ICON_UNMUTED =
    '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><path d="M19.07 4.93a10 10 0 010 14.14M15.54 8.46a5 5 0 010 7.08"/></svg>'

  // Derive a responsive srcset from a Shopify CDN image URL that already carries
  // a `width=` param (the pool bakes width:1200). Keeps client-rebuilt spotlight
  // and slide images right-sized instead of always downloading the 1200px source;
  // the lightbox builds its own <img> and is intentionally left full-size.
  const SPOTLIGHT_SRCSET_WIDTHS = [400, 600, 800, 1200, 1600]
  function buildSpotlightSrcset(url) {
    if (!url || !/[?&]width=\d+/.test(url)) return ''
    return SPOTLIGHT_SRCSET_WIDTHS.map(
      (w) => `${url.replace(/([?&])width=\d+/, `$1width=${w}`)} ${w}w`,
    ).join(', ')
  }

  function createSpotlightMedia(asset, eager, props, sizes) {
    if (asset.type === 'video') {
      const video = document.createElement('video')
      video.className = 'sai-emblag01__spotlight-media'
      video.src = asset.url
      const wantAutoplay = props ? props.autoplayVideo : true
      // Only the active slide (eager) autoplays; offscreen videos stay idle
      // (preload none, no autoplay) so a gallery of videos doesn't fetch them
      // all at once; the Embla `select` handler calls play() when one becomes
      // active. Eager slides preload metadata, not auto: play() streams on
      // demand, while preload auto starts a greedy whole-file download the
      // player then cancels — pure transfer waste. muted tracks the autoplay
      // intent so programmatic play() on activation is not blocked.
      video.autoplay = wantAutoplay && eager
      video.muted = wantAutoplay
      video.preload = eager ? 'metadata' : 'none'
      video.loop = true
      video.playsInline = true
      video.controls = false
      if (asset.thumb) video.poster = asset.thumb
      return video
    }
    const img = document.createElement('img')
    img.className = 'sai-emblag01__spotlight-media'
    const srcset = buildSpotlightSrcset(asset.url)
    if (sizes) img.sizes = sizes
    img.alt = asset.alt || ''
    if (asset.width) img.width = asset.width
    if (asset.height) img.height = asset.height
    if (eager) {
      // Active slide: load immediately (LCP).
      img.src = asset.url
      if (srcset) img.srcset = srcset
      img.loading = 'eager'
      img.fetchPriority = 'high'
    } else {
      // Offscreen slide: withhold the src. Native loading="lazy" does NOT defer a
      // horizontally-offset carousel slide (the browser keeps every slide in the
      // viewport's vertical band and fetches them all), so the real URL is parked on
      // data-sai-src until Embla's slidesInView promotes it — see _wireEmblaLazyLoad.
      img.dataset.saiSrc = asset.url
      if (srcset) img.dataset.saiSrcset = srcset
      img.loading = 'lazy'
    }
    return img
  }

  /**
   * Create custom play/pause overlay button for a video container.
   * Returns { playBtn, muteBtn } elements to be appended to the container.
   */
  function createVideoControls(video) {
    const playBtn = document.createElement('button')
    playBtn.className = 'sai-emblag01__video-play'
    playBtn.type = 'button'
    playBtn.setAttribute('aria-label', 'Play/Pause')
    playBtn.innerHTML = ICON_PAUSE
    /* Show play button initially only if video is paused */
    if (video.paused) {
      playBtn.innerHTML = ICON_PLAY
      playBtn.setAttribute('data-visible', '')
    }

    const muteBtn = document.createElement('button')
    muteBtn.className = 'sai-emblag01__video-mute'
    muteBtn.type = 'button'
    muteBtn.setAttribute('aria-label', 'Mute/Unmute')
    muteBtn.innerHTML = video.muted ? ICON_MUTED : ICON_UNMUTED
    muteBtn.setAttribute('data-visible', '')

    playBtn.addEventListener('click', (e) => {
      e.stopPropagation()
      if (video.paused) {
        video.play()
      } else {
        video.pause()
      }
    })

    muteBtn.addEventListener('click', (e) => {
      e.stopPropagation()
      video.muted = !video.muted
      muteBtn.innerHTML = video.muted ? ICON_MUTED : ICON_UNMUTED
    })

    video.addEventListener('play', () => {
      playBtn.innerHTML = ICON_PAUSE
      playBtn.removeAttribute('data-visible')
    })
    video.addEventListener('pause', () => {
      playBtn.innerHTML = ICON_PLAY
      playBtn.setAttribute('data-visible', '')
    })

    return { playBtn, muteBtn }
  }

  function createThumbMedia(asset) {
    const img = document.createElement('img')
    img.className = 'sai-emblag01__thumb-media'
    img.src = asset.thumb || asset.url
    img.alt = asset.alt || ''
    img.loading = 'lazy'
    return img
  }

  function createThumbPlay() {
    const play = document.createElement('span')
    play.className = 'sai-emblag01__thumb-play'
    play.setAttribute('aria-hidden', 'true')
    play.innerHTML =
      '<svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>'
    return play
  }

  function createThumbEl(asset, index, isActive) {
    const btn = document.createElement('button')
    btn.className = `sai-emblag01__thumb${isActive ? ' sai-emblag01__thumb--active' : ''}`
    btn.type = 'button'
    btn.dataset.index = index
    btn.dataset.handle = asset.handle
    btn.setAttribute('aria-label', `View image ${index + 1}`)

    btn.appendChild(createThumbMedia(asset))
    if (asset.type === 'video') btn.appendChild(createThumbPlay())
    return btn
  }

  if (!customElements.get(TAG)) {
    class SaiProductGalleryV2 extends HTMLElement {
      connectedCallback() {
        if (this._initialized) {
          // Reconnect (theme/Studio remove + reinsert): element/document listeners
          // persisted, so re-running the one-time setup would double-bind them.
          // Restore only what disconnectedCallback tore down — Embla, autoplay, and
          // the variant observer (its global handler was removed on disconnect;
          // without re-adding it later ?variant= changes stop updating the media).
          this._initEmbla()
          this._restartAutoplay()
          this._initVariantObserver()
          this._initLayoutObserver()
          return
        }
        this._initialized = true
        this._track = noopTrack
        this._activeIndex = 0
        this._assets = []
        this._allAssets = []
        this._pool = null
        this._currentVariantId = this.dataset.variantId || null
        this._activeFilters = {}
        this._pendingFilters = {}
        this._filterTags = {}

        // The carousel (swipe / momentum / snapping / infinite loop) is owned
        // entirely by Embla — see `_initEmbla`. `_embla` holds the instance.
        this._embla = null
        // Holds the source label of an in-flight programmatic Embla move
        // ('nav' for arrows / thumbnails / autoplay, 'dot' for dot clicks) so the
        // `select` handler attributes the view correctly; null during a bare user
        // drag, which reads as 'swipe'.
        this._programmaticNav = null

        this._props = {
          autoplay: this.dataset.autoplay === 'true',
          autoplayInterval: Number.parseInt(this.dataset.autoplayInterval, 10) || 5,
          swipeEnabled: this.dataset.swipeEnabled !== 'false',
          autoplayVideo: this.dataset.autoplayVideo !== 'false',
          showPlayPause: this.dataset.showPlayPause !== 'false',
          showLightboxThumbs: this.dataset.showLightboxThumbs !== 'false',
          showZoomControls: this.dataset.showZoomControls !== 'false',
          pinchToZoom: this.dataset.pinchToZoom !== 'false',
          spotlightObjectFit: this.dataset.spotlightObjectFit || 'contain',
          thumbnailObjectFit: this.dataset.thumbnailObjectFit || 'cover',
          lightboxObjectFit: this.dataset.lightboxObjectFit || 'contain',
          dotStyle: this.dataset.dotStyle === 'window' ? 'window' : 'strip',
        }

        this._selfBootstrap()
        this._initNav()
        this._initDots()
        this._initThumbs()
        this._initLightbox()
        this._initFilters()
        this._initVariantObserver()
        this._initLayoutObserver()
        this._applyProps()
      }

      // The track survives the breakpoint — only its box changes (full-bleed
      // below 768px, the grid's 1fr column above it). Embla caches slide
      // geometry at init, so it has to re-measure after that reflow or every
      // snap lands on stale offsets. Video controls re-sync because the layout
      // change can alter which controls are visible.
      _initLayoutObserver() {
        if (typeof matchMedia !== 'function') return
        if (!this._mql) this._mql = matchMedia(DESKTOP_MQ)
        if (!this._onLayoutChange) {
          this._onLayoutChange = () => {
            this._embla?.reInit()
            this._syncActiveVideo()
          }
        }
        this._mql.addEventListener('change', this._onLayoutChange)
      }

      _teardownLayoutObserver() {
        if (this._mql && this._onLayoutChange) {
          this._mql.removeEventListener('change', this._onLayoutChange)
        }
      }

      disconnectedCallback() {
        this._teardownVariantObserver()
        this._teardownLayoutObserver()
        if (this._autoplayTimer) clearInterval(this._autoplayTimer)
        this._teardownEmbla()
        // The lightbox has its own Embla instance; tear it down too so it doesn't
        // leak when the element is removed (e.g. Studio re-render) while open.
        this._teardownLightboxEmbla()
        this._removeFilterFromBody()
      }

      _applyProps() {
        if (!this._props.swipeEnabled) {
          const track = this.querySelector('.sai-emblag01__track')
          if (track) {
            track.style.overflowX = 'hidden'
            track.style.touchAction = 'pan-y'
          }
        }
        if (!this._props.pinchToZoom) {
          const wrap = this.querySelector('.sai-emblag01__spotlight-wrap')
          if (wrap) wrap.style.touchAction = 'pan-y'
        }
        if (!this._props.showZoomControls) {
          const zoomBtns = this.querySelectorAll('.sai-emblag01__zoom-trigger')
          for (const btn of zoomBtns) btn.style.display = 'none'
          // Zoom off disables the whole affordance, not just the icon: the
          // spotlight is no longer a click-to-zoom target (gated in
          // _openLightbox), so drop the pointer cursor that would otherwise
          // imply it opens something.
          const wraps = this.querySelectorAll('.sai-emblag01__spotlight-wrap')
          for (const wrap of wraps) wrap.style.cursor = 'default'
        }
        /* Apply object-fit as CSS variables for spotlight, thumbnails, and lightbox */
        if (this._props.spotlightObjectFit) {
          this.style.setProperty('--sai-emblag01-spotlight-fit', this._props.spotlightObjectFit)
        }
        if (this._props.thumbnailObjectFit) {
          this.style.setProperty('--sai-emblag01-thumb-fit', this._props.thumbnailObjectFit)
        }
        if (this._props.lightboxObjectFit) {
          this.style.setProperty('--sai-emblag01-lightbox-fit', this._props.lightboxObjectFit)
        }
      }

      setAnalytics(track) {
        this._track = typeof track === 'function' ? safeTrack(track) : noopTrack
      }

      _selfBootstrap() {
        const container = this.parentElement
        if (!container) return
        // The pool <script data-spectrum-snippet-pool> is a FOLLOWING sibling, so
        // at custom-element upgrade time (document still parsing) it may not be in
        // the DOM yet — readSnippetPool would return {} and variant-change would
        // have no pool to resolve against (the SSR gallery still renders, but
        // client re-resolution silently no-ops). Defer the read to DOMContentLoaded
        // while the document is still loading so every sibling is present.
        const read = () => {
          const pool = readSnippetPool(container)
          if (pool?.assets) this._applyPool(pool)
        }
        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', read, { once: true })
        } else {
          read()
        }
      }

      _applyPool(pool) {
        this._pool = pool
        const slot = resolveSlot(pool, this._currentVariantId)
        const slotVariant = resolveSlotVariant(slot)
        if (slotVariant?.assets) {
          this._allAssets = resolveAssets(pool, slotVariant.assets)
        }
        this._filterTags = buildFilterTags(slot?.filterTags)
        this._activeFilters = this._loadFilters()
        this._pendingFilters = {}
        this._applyFilters()
        this._renderFilterControls()
      }

      // ── Filters ──

      _initFilters() {
        this._filterDialog = this.querySelector('.sai-emblag01__filter-dialog')
        this._filterPanel = this._filterDialog?.querySelector('.sai-emblag01__filter-panel')

        if (this._filterDialog) {
          this._filterDialog.addEventListener('click', (e) => {
            if (e.target === this._filterDialog) this._closeFilterPanel()
          })
        }

        this.addEventListener('click', (e) => {
          if (e.target.closest('[data-action="toggle-filters"]')) {
            this._openFilterPanel()
            this._track(`${FEATURE_SLUG}:filter_open_elem_clicked`, {})
          }
        })

        // Scoped to this element, NOT document: the filter dialog is a DOM
        // descendant of this instance (a native <dialog> stays in the tree when
        // shown modally — top layer is rendering-only), so its control clicks
        // bubble here. Binding to document instead would let every gallery on a
        // page handle every other gallery's filter clicks, and would outlive the
        // instance on disconnect. An element listener is instance-scoped and dies
        // with the element.
        this.addEventListener('click', (e) => {
          if (e.target.closest('[data-action="close-filters"]')) {
            this._closeFilterPanel()
          }
          if (e.target.closest('[data-action="apply-filters"]')) {
            this._applyPendingFilters()
          }
          if (e.target.closest('[data-action="clear-filters"]')) {
            this._pendingFilters = {}
            this._activeFilters = {}
            this._applyFilters()
            this._saveFilters()
            this._closeFilterPanel()
            this._renderFilterControls()
            this._updateClearButton()
            this._track(`${FEATURE_SLUG}:filter_clear_elem_clicked`, {})
          }

          const item = e.target.closest('.sai-emblag01__filter-card, .sai-emblag01__filter-pill')
          if (item) {
            const dim = item.dataset.filterDim
            const val = item.dataset.filterVal
            if (!dim) return
            if (this._pendingFilters[dim] === val) {
              delete this._pendingFilters[dim]
            } else {
              this._pendingFilters[dim] = val
            }
            this._updateFilterItems()
            this._updateClearButton()
          }
        })
      }

      _renderFilterControls() {
        const filtersContainer = this.querySelector('.sai-emblag01__filters')
        const tabsContainer = this.querySelector('.sai-emblag01__filter-tabs')
        if (!filtersContainer) return

        const dims = Object.entries(this._filterTags)
        const filterBtns = this.querySelectorAll('.sai-emblag01__select-model')

        if (dims.length === 0 || this._allAssets.length <= 1) {
          for (const btn of filterBtns) btn.removeAttribute('data-has-filters')
          return
        }

        for (const btn of filterBtns) btn.setAttribute('data-has-filters', '')

        const dcImages = this._pool?.displayConfigImages || {}

        /* Build tabs */
        if (tabsContainer) {
          tabsContainer.innerHTML = ''
          for (let i = 0; i < dims.length; i++) {
            const [dim] = dims[i]
            const tab = document.createElement('button')
            tab.className = 'sai-emblag01__filter-tab'
            if (i === 0) tab.classList.add('sai-emblag01__filter-tab--active')
            tab.type = 'button'
            tab.dataset.filterTabDim = dim
            tab.textContent = dim
            /* Checkmark for dimensions with a pending selection */
            const check = document.createElement('span')
            check.className = 'sai-emblag01__filter-tab-check'
            check.textContent = ''
            tab.appendChild(check)
            tabsContainer.appendChild(tab)
          }

          tabsContainer.addEventListener('click', (e) => {
            const tabBtn = e.target.closest('.sai-emblag01__filter-tab')
            if (!tabBtn) return
            const dim = tabBtn.dataset.filterTabDim
            if (!dim) return
            /* Switch active tab */
            for (const t of tabsContainer.querySelectorAll('.sai-emblag01__filter-tab')) {
              t.classList.toggle('sai-emblag01__filter-tab--active', t.dataset.filterTabDim === dim)
            }
            /* Switch active content */
            const panel = this.querySelector('.sai-emblag01__filter-panel-body')
            if (panel) {
              for (const c of panel.querySelectorAll('.sai-emblag01__filter-content')) {
                c.classList.toggle(
                  'sai-emblag01__filter-content--active',
                  c.dataset.filterContentDim === dim,
                )
              }
            }
          })
        }

        /* Build content panels per dimension */
        filtersContainer.innerHTML = ''
        for (let i = 0; i < dims.length; i++) {
          const [dim, values] = dims[i]
          if (!Array.isArray(values) || values.length < 1) continue

          const content = document.createElement('div')
          content.className = 'sai-emblag01__filter-content'
          if (i === 0) content.classList.add('sai-emblag01__filter-content--active')
          content.dataset.filterContentDim = dim

          /* Determine rendering mode: grid (image/swatch) vs pills (text/null) */
          const hasVisual = values.some(
            (e) => e.displayConfig?.kind === 'image' || e.displayConfig?.kind === 'swatch',
          )

          if (hasVisual) {
            const grid = document.createElement('div')
            grid.className = 'sai-emblag01__filter-grid'

            for (const entry of values) {
              const val = entry.value
              const dc = entry.displayConfig
              const card = document.createElement('button')
              card.className = 'sai-emblag01__filter-card'
              card.type = 'button'
              card.dataset.filterDim = dim
              card.dataset.filterVal = val

              if (dc?.kind === 'image' && dc.value?.imageGid) {
                const img = document.createElement('img')
                img.className = 'sai-emblag01__filter-card-image'
                img.alt = val
                const imageUrl = dcImages[dc.value.imageGid]
                if (imageUrl) img.src = imageUrl
                card.appendChild(img)
              } else if (dc?.kind === 'swatch' && dc.value?.color) {
                const swatch = document.createElement('span')
                swatch.className = 'sai-emblag01__filter-card-swatch'
                swatch.style.backgroundColor = dc.value.color
                card.appendChild(swatch)
              }

              const label = document.createElement('span')
              label.className = 'sai-emblag01__filter-card-label'
              label.textContent = dc?.kind === 'text' && dc.value?.text ? dc.value.text : val
              card.appendChild(label)

              grid.appendChild(card)
            }

            content.appendChild(grid)
          } else {
            const pills = document.createElement('div')
            pills.className = 'sai-emblag01__filter-pills'

            for (const entry of values) {
              const val = entry.value
              const dc = entry.displayConfig
              const pill = document.createElement('button')
              pill.className = 'sai-emblag01__filter-pill'
              pill.type = 'button'
              pill.dataset.filterDim = dim
              pill.dataset.filterVal = val
              pill.textContent = dc?.kind === 'text' && dc.value?.text ? dc.value.text : val
              pills.appendChild(pill)
            }

            content.appendChild(pills)
          }

          filtersContainer.appendChild(content)
        }
      }

      _openFilterPanel() {
        const dialog = this._filterDialog || this.querySelector('.sai-emblag01__filter-dialog')
        if (!dialog) return
        this._filterDialog = dialog
        this._pendingFilters = { ...this._activeFilters }
        this._renderFilterControls()
        document.documentElement.style.overflow = 'hidden'
        dialog.showModal()
        this._updateFilterItems()
        this._updateClearButton()
      }

      _closeFilterPanel() {
        const dialog = this._filterDialog || this.querySelector('.sai-emblag01__filter-dialog')
        if (!dialog) return
        dialog.close()
        document.documentElement.style.overflow = ''
      }

      _removeFilterFromBody() {
        document.documentElement.style.overflow = ''
      }

      _applyPendingFilters() {
        this._activeFilters = { ...this._pendingFilters }
        this._applyFilters()
        this._saveFilters()
        this._closeFilterPanel()
        this._track(`${FEATURE_SLUG}:filter_apply_elem_clicked`, { active_filters: { ...this._activeFilters } })
      }

      _updateFilterItems() {
        const root = this._filterPanel || this
        const items = root.querySelectorAll(
          '.sai-emblag01__filter-card, .sai-emblag01__filter-pill',
        )
        for (const item of items) {
          const dim = item.dataset.filterDim
          const val = item.dataset.filterVal
          const isActive = this._pendingFilters[dim] === val
          if (item.classList.contains('sai-emblag01__filter-card')) {
            item.classList.toggle('sai-emblag01__filter-card--active', isActive)
          } else {
            item.classList.toggle('sai-emblag01__filter-pill--active', isActive)
          }
        }
        this._updateTabChecks()
      }

      _updateTabChecks() {
        const tabsContainer = this.querySelector('.sai-emblag01__filter-tabs')
        if (!tabsContainer) return
        for (const tab of tabsContainer.querySelectorAll('.sai-emblag01__filter-tab')) {
          const dim = tab.dataset.filterTabDim
          const check = tab.querySelector('.sai-emblag01__filter-tab-check')
          if (check) check.textContent = this._pendingFilters[dim] ? '✓' : ''
        }
      }

      _updateClearButton() {
        const root = this._filterPanel || this
        const clearBtn = root.querySelector('.sai-emblag01__filter-panel-clear')
        if (!clearBtn) return
        clearBtn.style.display = Object.keys(this._pendingFilters).length > 0 ? '' : 'none'
      }

      _saveFilters() {
        try {
          if (Object.keys(this._activeFilters).length === 0) {
            localStorage.removeItem(FILTER_STORAGE_KEY)
          } else {
            localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(this._activeFilters))
          }
        } catch (_) {}
      }

      _loadFilters() {
        try {
          const stored = localStorage.getItem(FILTER_STORAGE_KEY)
          if (!stored) return {}
          const filters = JSON.parse(stored)
          if (!filters || typeof filters !== 'object') return {}
          const valid = {}
          for (const [key, val] of Object.entries(filters)) {
            const entries = this._filterTags[key]
            if (!entries) continue
            const match = entries.some((e) => (typeof e === 'string' ? e === val : e.value === val))
            if (match) valid[key] = val
          }
          return valid
        } catch (_) {
          return {}
        }
      }

      _applyFilters() {
        this._assets = filterAssets(this._allAssets, this._activeFilters)
        // A filter set that resolves to nothing falls back to the full asset set
        // rather than an empty state — the gallery is never intentionally blanked,
        // so there is no hide/show of the stage to manage here.
        if (this._assets.length === 0) {
          this._assets = this._allAssets.slice()
          this._activeFilters = {}
        }
        this._rebuildGallery()
      }

      // ── Navigation ──

      _initNav() {
        this.addEventListener('click', (e) => {
          const action = e.target.closest('[data-action]')?.dataset.action
          if (action === 'prev') this._goTo(this._activeIndex - 1)
          if (action === 'next') this._goTo(this._activeIndex + 1)
        })
      }

      _goTo(index, navSource = 'nav') {
        // Embla (on the visible layout's track) performs the move; its `select` event
        // is the single owner of `_activeIndex`, indicators, video, and analytics — so
        // arrows / dots / thumbnails and swipes all funnel through one path. Without
        // Embla (a single-slide gallery, or the shared engine failed to load) the
        // gallery is a static first slide, so navigation is a no-op.
        if (this._assets.length === 0 || !this._embla) return
        const count = this._assets.length
        const next = ((index % count) + count) % count
        // Load the target before scrolling so a jump to a far slide (thumbnail click)
        // lands on a ready image instead of a blank.
        this._loadSlideImg(next)
        // Mark the move programmatic so `select` labels the view `navSource` (not
        // `swipe`). Embla emits `select` synchronously inside `scrollTo`, so the flag
        // is read before we clear it.
        this._programmaticNav = navSource
        this._embla.scrollTo(next)
        this._programmaticNav = null
      }

      // The slide element for a logical index (no clones — Embla owns loop).
      _slideEl(i) {
        const c = this.querySelector('.sai-emblag01__container')
        return c ? c.children[i] : null
      }

      // Capture a track's existing gallery <video>s, keyed by their slide handle,
      // BEFORE the track is cleared. A rebuild that keeps the same video (a shared
      // hero video across a variant swap, where only the surrounding images change)
      // can then re-adopt the live element instead of building a fresh one — an
      // orphaned <video> keeps its currentTime + mute state, so the video resumes
      // where it was instead of jumping back to the start. Returns a handle→element
      // map consumed by the slide loop.
      _harvestTrackVideos(track) {
        const kept = new Map()
        for (const video of track.querySelectorAll('video.sai-emblag01__spotlight-media')) {
          const handle = video.closest('[data-handle]')?.dataset.handle
          if (!handle || kept.has(handle)) continue
          // Remember whether it was playing so a manually-started video also
          // resumes (autoplay galleries resume unconditionally, see below).
          video.__saiWasPlaying = !video.paused && !video.ended
          kept.set(handle, video)
        }
        return kept
      }

      // After a rebuild the select handler short-circuits (i === _activeIndex) and a
      // reused <video> is left paused from its brief detach — nothing restarts it. So
      // play the active slide's video explicitly: a reused element resumes from its
      // preserved currentTime (no jump to 0), a fresh one starts at 0 exactly as its
      // autoplay attribute would. Gated so a non-autoplay gallery only resumes a
      // video the shopper had actually started.
      _resumeActiveVideo() {
        const container = this.querySelector('.sai-emblag01__container')
        const activeSlide = container?.children[this._activeIndex]
        // Pause every other track video first: a variant swap can relocate a
        // still-playing video's handle off the active index, and Embla's init
        // emits no 'select', so the settle-time blanket pause never fires on the
        // rebuild path. Without this the relocated video decodes off-screen until
        // the next navigation. The active video keeps its shared-handoff resume.
        this._pauseInactiveVideos(container, activeSlide)
        const video = activeSlide?.querySelector('video')
        if (!video) return
        const shouldPlay = video.__saiWasPlaying || this._props.autoplayVideo
        video.__saiWasPlaying = false
        // play() returns a promise in browsers but nothing under jsdom — guard both.
        if (shouldPlay) {
          const p = video.play?.()
          if (p && typeof p.catch === 'function') p.catch(() => {})
        }
      }

      // Rebuild the Embla track's slides from the current (filtered/variant) asset
      // set. The track is the Embla viewport, so the instance is torn down before
      // its DOM is replaced and re-created once the new `viewport > container >
      // slides` tree is in place. First slide eager (LCP), the rest parked on
      // data-sai-src until Embla's slidesInView promotes them.
      _rebuildSlides() {
        const track = this.querySelector('.sai-emblag01__track')
        if (!track) return
        // Keep any same-handle videos alive across the clear below (see
        // _harvestTrackVideos) so a variant swap doesn't restart the hero video.
        const keptVideos = this._harvestTrackVideos(track)
        this._teardownEmbla()
        track.innerHTML = ''
        // Controls are positioned on the wrapper (not the slide) so they stay
        // in-viewport as the track scrolls; the outgoing set is stale now.
        const wrap = this.querySelector('.sai-emblag01__spotlight-wrap')
        if (wrap) {
          for (const old of wrap.querySelectorAll(
            '.sai-emblag01__video-play, .sai-emblag01__video-mute',
          ))
            old.remove()
        }
        const container = document.createElement('div')
        container.className = 'sai-emblag01__container'
        for (let i = 0; i < this._assets.length; i++) {
          const slide = document.createElement('div')
          slide.className = 'sai-emblag01__slide'
          slide.dataset.index = i
          slide.dataset.handle = this._assets[i].handle
          const asset = this._assets[i]
          const reused = asset.type === 'video' ? keptVideos.get(asset.handle) : null
          slide.appendChild(
            reused || createSpotlightMedia(asset, i === 0, this._props, SPOTLIGHT_SIZES),
          )
          container.appendChild(slide)
        }
        track.appendChild(container)
        for (let i = 0; i < this._assets.length; i++) {
          const slide = container.children[i]
          if (!slide) continue
          for (const video of slide.querySelectorAll('video')) this._bindVideoTracking(video)
        }
        this._updateVideoControls()
        // Hand the swipe/loop/momentum entirely to Embla.
        this._initEmbla()
        // Re-sync autoplay to the new slide count: a rebuild that grows past one
        // slide must start it; one that shrinks to a single slide must clear the
        // stale timer. `_restartAutoplay` does both (clears first, then guards on
        // count), so calling it unconditionally after every rebuild is correct.
        this._restartAutoplay()
      }

      /**
       * Refresh the play/pause + mute buttons on the spotlight wrapper for the
       * currently active slide. Controls live on the wrapper (not the slide) so
       * they stay in-viewport as the track scrolls.
       */
      _updateVideoControls() {
        const wrap = this.querySelector('.sai-emblag01__spotlight-wrap')
        if (!wrap) return
        for (const old of wrap.querySelectorAll(
          '.sai-emblag01__video-play, .sai-emblag01__video-mute',
        ))
          old.remove()
        const video = this._slideEl(this._activeIndex)?.querySelector('video')
        if (video) this._attachVideoControls(wrap, video)
      }

      // Pause every track video except the one on `activeSlide`.
      _pauseInactiveVideos(container, activeSlide) {
        if (!container) return
        for (const slide of container.children) {
          if (slide === activeSlide) continue
          const v = slide.querySelector('video')
          if (v) v.pause()
        }
      }

      _playActiveVideo() {
        const container = this.querySelector('.sai-emblag01__container')
        if (!container) return
        const activeSlide = container.children[this._activeIndex]
        this._pauseInactiveVideos(container, activeSlide)
        const v = activeSlide?.querySelector('video')
        if (v && this._props.autoplayVideo) v.play().catch(() => {})
      }

      // Promote a slide image parked on data-sai-src to a real, loading src (no-op
      // once loaded). The first slide ships a real src at SSR (LCP), so it is
      // never parked.
      _loadSlideImg(index) {
        const img = this._slideEl(index)?.querySelector('img[data-sai-src]')
        if (!img) return
        img.src = img.dataset.saiSrc
        if (img.dataset.saiSrcset) img.srcset = img.dataset.saiSrcset
        delete img.dataset.saiSrc
        delete img.dataset.saiSrcset
      }

      // Embla lazy-load (the library's official pattern): load only the slides Embla
      // reports in view — plus their immediate neighbours for instant next/prev — via
      // the `slidesInView` event. Required because native loading="lazy" does not
      // defer offscreen carousel slides (an offscreen slide sits in the viewport's
      // vertical band, so the browser fetches it regardless of horizontal offset).
      _wireEmblaLazyLoad() {
        const embla = this._embla
        if (!embla || typeof embla.slidesInView !== 'function') return
        const count = this._assets.length
        const load = () => {
          const seen = new Set()
          for (const i of embla.slidesInView()) {
            for (const j of [i, i + 1, i - 1]) {
              const k = ((j % count) + count) % count
              if (!seen.has(k)) {
                seen.add(k)
                this._loadSlideImg(k)
              }
            }
          }
        }
        embla.on('slidesInView', load)
        load()
      }

      // Wire video controls + autoplay for the active slide. Called from the Embla
      // `select` handler and on breakpoint change.
      _syncActiveVideo() {
        // Image-only galleries have no video state to sync; skip the per-slide
        // walk that every swipe would otherwise pay (one track query vs a loop).
        if (!this.querySelector('.sai-emblag01__container video')) return
        this._updateVideoControls()
        this._playActiveVideo()
      }

      _isActiveSlideVideoPlaying() {
        const slide = this._slideEl(this._activeIndex)
        if (!slide) return false
        for (const video of slide.querySelectorAll('video')) {
          if (!video.paused && !video.ended) return true
        }
        return false
      }

      _bindVideoTracking(video) {
        // Idempotent: a <video> reused across a rebuild (same handle — e.g. a
        // shared hero video kept while a variant swap only changes the surrounding
        // images) keeps its listeners. Re-binding would stack duplicate analytics
        // and autoplay-timer handlers on the same element.
        if (video.__saiTracked) return
        video.__saiTracked = true
        // Read the slide index live from the DOM at event time rather than
        // capturing it at bind time: a reused video's listeners outlive the
        // rebuild, and a variant swap can move that video to a different index, so
        // a captured index would misreport the gallery position in analytics.
        const indexOf = () => Number(video.closest('[data-index]')?.dataset.index ?? -1)
        video.addEventListener('play', () => {
          this._track(`${FEATURE_SLUG}:video_play_elem_clicked`, { index: indexOf() })
          if (this._autoplayTimer) {
            clearInterval(this._autoplayTimer)
            this._autoplayTimer = null
          }
        })
        video.addEventListener('pause', () => {
          this._track(`${FEATURE_SLUG}:video_pause_elem_clicked`, { index: indexOf() })
          this._restartAutoplay()
        })
        video.addEventListener('ended', () => {
          this._restartAutoplay()
        })
      }

      /**
       * Attach custom play/pause + mute buttons to a positioned container
       * that holds (or is an ancestor of) the given video element.
       */
      _attachVideoControls(container, video) {
        if (!this._props.showPlayPause) return
        const { playBtn, muteBtn } = createVideoControls(video)
        container.appendChild(playBtn)
        container.appendChild(muteBtn)
      }

      // ── Thumbnails ──

      _initThumbs() {
        // One strip for every viewport (a vertical rail beside the spotlight, a
        // horizontal row beneath it when mobile_nav = thumbnails).
        const container = this.querySelector('.sai-emblag01__thumbs')
        if (!container) return
        container.addEventListener('click', (e) => {
          const thumb = e.target.closest('.sai-emblag01__thumb')
          if (!thumb) return
          const idx = Number.parseInt(thumb.dataset.index, 10)
          if (!Number.isNaN(idx)) this._goTo(idx)
        })
      }

      _updateThumbs() {
        const thumbs = this.querySelectorAll('.sai-emblag01__thumbs .sai-emblag01__thumb')
        for (const t of thumbs) {
          const idx = Number.parseInt(t.dataset.index, 10)
          t.classList.toggle('sai-emblag01__thumb--active', idx === this._activeIndex)
        }
        this._scrollThumbIntoView()
      }

      _scrollThumbIntoView() {
        const active = this.querySelector('.sai-emblag01__thumbs .sai-emblag01__thumb--active')
        if (!active) return
        // One strip, two scroll axes: a vertical rail from 768px, a horizontal
        // row below it. `inline: 'center'` centres the row's active thumb; on
        // the vertical rail it would scroll ancestors horizontally for nothing.
        // `block: 'nearest'` avoids yanking the page vertically either way.
        // Reuse the layout observer's MediaQueryList rather than allocating a new
        // one on every settle; fall back only if the observer never ran.
        const twoColumn = this._mql
          ? this._mql.matches
          : typeof matchMedia === 'function' && matchMedia(DESKTOP_MQ).matches
        active.scrollIntoView({
          block: 'nearest',
          inline: twoColumn ? 'nearest' : 'center',
          behavior: 'smooth',
        })
      }

      // ── Dots ──

      _updateDots() {
        this._highlightDot(this._activeIndex)
      }

      // Indicator-only dot highlight, keyed on an explicit index. Split from
      // `_activeIndex` so the continuous scroll handler can track the finger
      // live without claiming the active slide (that stays the settle handler's
      // job). Centres the active dot when the band overflows.
      _highlightDot(index) {
        const container = this.querySelector('.sai-emblag01__dots')
        if (!container) return
        if (this._props?.dotStyle === 'window') {
          this._highlightDotWindow(container, index)
          return
        }
        container.classList.remove('sai-emblag01__dots--win')
        const dots = container.querySelectorAll('.sai-emblag01__dot')
        // Uniform dots — only the active changes colour. The strip scrolls when
        // it overflows the rail; keep the active dot in view by nudging
        // scrollLeft, but only when it actually falls outside the rail (no-op
        // while it's already visible, so the strip doesn't jump on every step).
        let active = null
        for (const d of dots) {
          const idx = Number.parseInt(d.dataset.index, 10)
          const isActive = idx === index
          d.classList.toggle('sai-emblag01__dot--active', isActive)
          if (isActive) active = d
        }
        if (!active) return
        const cRect = container.getBoundingClientRect()
        const aRect = active.getBoundingClientRect()
        if (aRect.left < cRect.left) {
          container.scrollLeft -= cRect.left - aRect.left + DOT_SCROLL_PAD
        } else if (aRect.right > cRect.right) {
          container.scrollLeft += aRect.right - cRect.right + DOT_SCROLL_PAD
        }
      }

      // Windowed pagination: fixed 5-dot window, active centred, outer dots
      // shrink where slides overflow the window. Out-of-window dots collapse to
      // zero (not scrolled), so the band never overflows and needs no scrollLeft
      // nudging. Each dot carries a single `--win*` class (the sizing lives on
      // those single-class selectors); the strip-mode `--active` marker is cleared
      // so its accent colour never leaks onto the dark pill. The container gets
      // `--dots--win` (gap 0; spacing moves to the dots' own margin) so a
      // collapsing dot animates smoothly to zero width without a leftover gap.
      _highlightDotWindow(container, index) {
        const dots = [...container.querySelectorAll('.sai-emblag01__dot')]
        // A fresh dot set snaps into the window with no transition; only slide
        // changes on an already-windowed set animate. Keyed on the dots, not the
        // container: the SSR now paints the window (dots carry --win), and a
        // rebuild recreates plain dots while leaving the container's --win class —
        // both must be detected so neither flashes an 8px-strip → window animation.
        const firstPaint = !dots[0]?.classList.contains('sai-emblag01__dot--win')
        container.classList.add('sai-emblag01__dots--win')
        if (firstPaint) container.classList.add('sai-emblag01__dots--win-noanim')
        const labels = computeDotWindow(dots.length, index)
        dots.forEach((d, i) => {
          const label = labels[i]
          d.classList.remove('sai-emblag01__dot--active')
          d.classList.add('sai-emblag01__dot--win')
          d.classList.toggle('sai-emblag01__dot--win-active', label === 'pill')
          d.classList.toggle('sai-emblag01__dot--win-sm', label === 'small')
          d.classList.toggle('sai-emblag01__dot--win-hidden', label === 'hidden')
        })
        if (firstPaint && typeof requestAnimationFrame === 'function') {
          void container.offsetWidth // flush the snapped layout before enabling motion
          requestAnimationFrame(() => container.classList.remove('sai-emblag01__dots--win-noanim'))
        }
      }

      // ── Dots ──

      _initDots() {
        // Embla is created in `_rebuildSlides` / `_rebuildGallery`; here we just
        // wire the dots to it and start autoplay. Dot clicks route through
        // `_goTo` → Embla.
        const dotsContainer = this.querySelector('.sai-emblag01__dots')
        if (dotsContainer && !dotsContainer._saiDotsBound) {
          dotsContainer._saiDotsBound = true
          dotsContainer.addEventListener('click', (e) => {
            const dot = e.target.closest('.sai-emblag01__dot')
            if (!dot) return
            const idx = Number.parseInt(dot.dataset.index, 10)
            if (!Number.isNaN(idx)) this._goTo(idx, 'dot')
          })
        }
        this._restartAutoplay()
      }

      _restartAutoplay() {
        if (this._autoplayTimer) {
          clearInterval(this._autoplayTimer)
          this._autoplayTimer = null
        }
        if (!this._props.autoplay || this._assets.length <= 1 || this._isActiveSlideVideoPlaying())
          return
        this._autoplayTimer = setInterval(() => {
          if (this._assets.length <= 1 || this._isActiveSlideVideoPlaying()) return
          this._goTo(this._activeIndex + 1)
        }, this._props.autoplayInterval * 1000)
      }

      // ── Lightbox ──

      _initLightbox() {
        this._lightboxIndex = 0

        this.addEventListener('click', (e) => {
          const action = e.target.closest('[data-action]')?.dataset.action
          if (action === 'open-lightbox') this._openLightbox()
          if (action === 'close-lightbox') this._closeLightbox()
          if (action === 'lightbox-prev') this._lightboxGoTo(this._lightboxIndex - 1)
          if (action === 'lightbox-next') this._lightboxGoTo(this._lightboxIndex + 1)

          // The whole spotlight image is a zoom affordance, not just the zoom
          // button — a bare click on the media (anything that isn't an overlay
          // control) opens the lightbox. Buttons/links are excluded so nav
          // chevrons, the filter button, and video play/mute keep their own
          // behaviour. A finger drag is an Embla pointer gesture, not a click.
          if (
            !action &&
            e.target.closest('.sai-emblag01__spotlight-wrap') &&
            !e.target.closest('button, a')
          ) {
            this._openLightbox()
          }
        })

        const dialog = this.querySelector('.sai-emblag01__lightbox')
        if (dialog) {
          dialog.addEventListener('click', (e) => {
            if (e.target === dialog) this._closeLightbox()
          })
          dialog.addEventListener('close', () => {
            document.documentElement.style.overflow = ''
          })
        }

        const thumbsContainer = this.querySelector('.sai-emblag01__lightbox-thumbs')
        if (thumbsContainer) {
          thumbsContainer.addEventListener('click', (e) => {
            const thumb = e.target.closest('.sai-emblag01__lightbox-thumb')
            if (!thumb) return
            const idx = Number.parseInt(thumb.dataset.index, 10)
            if (!Number.isNaN(idx)) this._lightboxGoTo(idx)
          })
        }
      }

      _openLightbox() {
        // Single choke point for the fullscreen zoom dialog. `show_zoom_controls`
        // off hides the icon AND makes the gallery non-zoomable: every open path
        // (the zoom-icon action and a bare spotlight click) routes through here,
        // so one guard disables the dialog entirely.
        if (!this._props.showZoomControls) return
        const dialog = this.querySelector('.sai-emblag01__lightbox')
        if (!dialog) return

        this._lightboxIndex = this._activeIndex
        const thumbsContainer = this.querySelector('.sai-emblag01__lightbox-thumbs')
        if (this._props.showLightboxThumbs) {
          this._buildLightboxThumbs()
          if (thumbsContainer) thumbsContainer.style.display = ''
        } else {
          if (thumbsContainer) thumbsContainer.style.display = 'none'
        }
        this._buildLightboxTrack()

        const navBtns = dialog.querySelectorAll('.sai-emblag01__lightbox-nav')
        for (const btn of navBtns) {
          btn.style.display = this._assets.length <= 1 ? 'none' : ''
        }

        document.documentElement.style.overflow = 'hidden'
        dialog.showModal()
        // Embla measures against a laid-out viewport, so attach after showModal.
        this._initLightboxEmbla()

        if (this._props.pinchToZoom) {
          const spotlight = this.querySelector('.sai-emblag01__lightbox-spotlight')
          if (spotlight && !spotlight._zoomInitialized) {
            this._initLightboxZoom(spotlight)
            spotlight._zoomInitialized = true
          }
        }

        this._lightboxOpenTime = Date.now()
        this._track(`${FEATURE_SLUG}:lightbox_open_elem_clicked`, { asset_count: this._assets.length })
      }

      _closeLightbox() {
        const dialog = this.querySelector('.sai-emblag01__lightbox')
        if (!dialog) return
        for (const v of dialog.querySelectorAll('video')) v.pause()
        this._teardownLightboxEmbla()
        dialog.close()
        document.documentElement.style.overflow = ''
        const dwell = this._lightboxOpenTime ? Date.now() - this._lightboxOpenTime : 0
        this._track(`${FEATURE_SLUG}:lightbox_close_elem_clicked`, { dwell_ms: dwell })
      }

      _lightboxGoTo(index) {
        if (this._assets.length === 0) return
        const count = this._assets.length
        const next = ((index % count) + count) % count
        if (this._lightboxEmbla) {
          // Embla scrolls the pre-rendered track; its select handler owns
          // _lightboxIndex, the active thumbnail, zoom reset, and video.
          this._loadLightboxSlideImg(next)
          this._lightboxEmbla.scrollTo(next)
          return
        }
        // No Embla (single slide / degraded): update indicators directly.
        this._lightboxIndex = next
        this._updateLightboxThumbs()
      }

      _buildLightboxThumbs() {
        const container = this.querySelector('.sai-emblag01__lightbox-thumbs')
        if (!container) return
        container.innerHTML = ''
        for (let i = 0; i < this._assets.length; i++) {
          const asset = this._assets[i]
          const btn = document.createElement('button')
          btn.className = `sai-emblag01__lightbox-thumb${i === this._lightboxIndex ? ' sai-emblag01__lightbox-thumb--active' : ''}`
          btn.type = 'button'
          btn.dataset.index = i
          btn.setAttribute('aria-label', `View image ${i + 1}`)
          const img = document.createElement('img')
          img.src = asset.thumb || asset.url
          img.alt = asset.alt || ''
          img.loading = 'lazy'
          btn.appendChild(img)
          container.appendChild(btn)
        }
      }

      // The lightbox spotlight is a pre-rendered Embla track (full-size slides).
      // Navigation scrolls to an existing slide — no rebuild, no refetch — matching
      // the main gallery. Drag is disabled (see _initLightboxEmbla) so pinch-zoom
      // owns the pointer; navigation is via the chevrons + thumbnail strip.
      _buildLightboxTrack() {
        const spotlight = this.querySelector('.sai-emblag01__lightbox-spotlight')
        if (!spotlight) return
        const container = document.createElement('div')
        container.className = 'sai-emblag01__lightbox-container'
        for (let i = 0; i < this._assets.length; i++) {
          const asset = this._assets[i]
          const slide = document.createElement('div')
          slide.className = 'sai-emblag01__lightbox-slide'
          slide.dataset.index = i
          const eager = i === this._lightboxIndex
          if (asset.type === 'video') {
            const wrap = document.createElement('div')
            wrap.className = 'sai-emblag01__lightbox-video-wrap'
            const video = document.createElement('video')
            video.src = asset.url
            video.preload = eager ? 'metadata' : 'none'
            video.autoplay = eager && this._props.autoplayVideo
            video.muted = this._props.autoplayVideo
            video.loop = true
            video.playsInline = true
            video.controls = false
            if (asset.thumb) video.poster = asset.thumb
            wrap.appendChild(video)
            slide.appendChild(wrap)
            this._bindVideoTracking(video)
            this._attachVideoControls(wrap, video)
          } else {
            const img = document.createElement('img')
            img.alt = asset.alt || ''
            img.draggable = false
            // Full-size images: withhold the src for offscreen slides (Embla's
            // slidesInView promotes it) so opening the lightbox doesn't fetch all.
            if (eager) {
              img.src = asset.url
            } else {
              img.dataset.saiSrc = asset.url
              img.loading = 'lazy'
            }
            slide.appendChild(img)
          }
          container.appendChild(slide)
        }
        spotlight.replaceChildren(container)
      }

      _lightboxSlideEl(i) {
        const c = this.querySelector('.sai-emblag01__lightbox-container')
        return c ? c.children[i] : null
      }

      _loadLightboxSlideImg(i) {
        const img = this._lightboxSlideEl(i)?.querySelector('img[data-sai-src]')
        if (!img) return
        img.src = img.dataset.saiSrc
        delete img.dataset.saiSrc
      }

      _teardownLightboxEmbla() {
        this._lightboxEmblaToken = (this._lightboxEmblaToken || 0) + 1
        if (this._lightboxEmbla) {
          try {
            this._lightboxEmbla.destroy()
          } catch (_) {}
          this._lightboxEmbla = null
        }
      }

      async _initLightboxEmbla() {
        this._teardownLightboxEmbla()
        const viewport = this.querySelector('.sai-emblag01__lightbox-spotlight')
        if (!viewport || this._assets.length <= 1) return
        const token = this._lightboxEmblaToken
        const Engine = globalThis.__SAI_TEST_HARNESS__ ? null : await ensureCarouselEngine()
        if (token !== this._lightboxEmblaToken || !this.isConnected) return
        try {
          this._lightboxEmbla = this._createEmbla(
            viewport,
            {
              startIndex: this._lightboxIndex,
              align: 'center',
              containScroll: false,
              loop: true,
              // No carousel drag in the lightbox — pinch-to-zoom owns the pointer.
              // Navigation is chevrons + thumbnails, which call scrollTo().
              watchDrag: false,
              duration: 22,
            },
            Engine,
          )
        } catch (_) {
          this._lightboxEmbla = null
        }
        if (!this._lightboxEmbla || typeof this._lightboxEmbla.on !== 'function') {
          this._lightboxEmbla = null
          return
        }
        this._onLightboxSelect = () => {
          const i = this._lightboxEmbla.selectedScrollSnap()
          if (i === this._lightboxIndex) return
          for (const v of viewport.querySelectorAll('video')) v.pause()
          this._lightboxIndex = i
          if (this._resetLightboxZoom) this._resetLightboxZoom()
          this._updateLightboxThumbs()
          const video = this._lightboxSlideEl(i)?.querySelector('video')
          if (video && this._props.autoplayVideo) video.play().catch(() => {})
        }
        this._lightboxEmbla.on('select', this._onLightboxSelect)
        this._lightboxEmbla.on('reInit', this._onLightboxSelect)
        this._wireLightboxLazyLoad()
      }

      _wireLightboxLazyLoad() {
        const embla = this._lightboxEmbla
        if (!embla || typeof embla.slidesInView !== 'function') return
        const count = this._assets.length
        const load = () => {
          const seen = new Set()
          for (const idx of embla.slidesInView()) {
            for (const j of [idx, idx + 1, idx - 1]) {
              const k = ((j % count) + count) % count
              if (!seen.has(k)) {
                seen.add(k)
                this._loadLightboxSlideImg(k)
              }
            }
          }
        }
        embla.on('slidesInView', load)
        load()
      }

      _initLightboxZoom(container) {
        let scale = 1
        let translateX = 0
        let translateY = 0
        let startDist = 0
        let startScale = 1
        let isPinching = false
        const pointers = new Map()
        // Zoom targets the ACTIVE slide's media (the lightbox is now a track of
        // slides, not a single element). The zoom transform lives on the <img>
        // while Embla transforms the track container — different elements, so they
        // compose without conflict.
        const media = () => {
          const slide = container.querySelector(
            '.sai-emblag01__lightbox-slide[data-index="' + this._lightboxIndex + '"]',
          )
          return (slide || container).querySelector('img, video, .sai-emblag01__lightbox-video-wrap')
        }

        this._resetLightboxZoom = () => {
          scale = 1
          translateX = 0
          translateY = 0
          // Clear every slide's transform — navigating away from a zoomed slide must
          // leave it un-zoomed when it scrolls back into view.
          for (const el of container.querySelectorAll(
            'img, video, .sai-emblag01__lightbox-video-wrap',
          )) {
            el.style.transform = ''
            el.style.cursor = ''
          }
        }

        const clampTranslate = () => {
          if (scale <= 1) {
            translateX = 0
            translateY = 0
            return
          }
          const maxX = (container.offsetWidth * (scale - 1)) / 2
          const maxY = (container.offsetHeight * (scale - 1)) / 2
          translateX = Math.max(-maxX, Math.min(maxX, translateX))
          translateY = Math.max(-maxY, Math.min(maxY, translateY))
        }

        const apply = () => {
          const el = media()
          if (!el) return
          if (scale <= 1) {
            el.style.transform = ''
            el.style.cursor = ''
            translateX = 0
            translateY = 0
            scale = 1
          } else {
            el.style.transform = `translate(${translateX}px, ${translateY}px) scale(${scale})`
            el.style.cursor = 'grab'
          }
        }

        const dist = () => {
          const pts = Array.from(pointers.values())
          if (pts.length < 2) return 0
          return Math.hypot(pts[1].x - pts[0].x, pts[1].y - pts[0].y)
        }

        container.addEventListener('pointerdown', (e) => {
          pointers.set(e.pointerId, { x: e.clientX, y: e.clientY })
          if (pointers.size === 2) {
            isPinching = true
            startDist = dist()
            startScale = scale
          }
        })

        container.addEventListener('pointermove', (e) => {
          if (!pointers.has(e.pointerId)) return
          pointers.set(e.pointerId, { x: e.clientX, y: e.clientY })
          if (isPinching && pointers.size === 2) {
            const d = dist()
            if (startDist > 0) {
              scale = Math.max(1, Math.min(4, startScale * (d / startDist)))
              clampTranslate()
              apply()
            }
          } else if (pointers.size === 1 && scale > 1) {
            const ptr = pointers.get(e.pointerId)
            translateX += e.movementX
            translateY += e.movementY
            clampTranslate()
            apply()
          }
        })

        const pointerUp = (e) => {
          pointers.delete(e.pointerId)
          if (pointers.size < 2) isPinching = false
        }
        container.addEventListener('pointerup', pointerUp)
        container.addEventListener('pointercancel', pointerUp)

        container.addEventListener(
          'wheel',
          (e) => {
            e.preventDefault()
            const delta = e.deltaY > 0 ? 0.9 : 1.1
            scale = Math.max(1, Math.min(4, scale * delta))
            clampTranslate()
            apply()
          },
          { passive: false },
        )

        container.addEventListener('dblclick', (e) => {
          if (scale > 1) {
            scale = 1
            translateX = 0
            translateY = 0
          } else {
            scale = 2
          }
          apply()
        })

        container.style.touchAction = 'none'
        container.style.userSelect = 'none'
        container.style.webkitUserSelect = 'none'
        container.addEventListener('dragstart', (e) => e.preventDefault())
      }

      _updateLightboxThumbs() {
        const thumbs = this.querySelectorAll('.sai-emblag01__lightbox-thumb')
        for (const t of thumbs) {
          const idx = Number.parseInt(t.dataset.index, 10)
          t.classList.toggle('sai-emblag01__lightbox-thumb--active', idx === this._lightboxIndex)
        }
        const active = this.querySelector('.sai-emblag01__lightbox-thumb--active')
        if (active)
          active.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'smooth' })
      }

      // ── Variant change ──

      _initVariantObserver() {
        const checkVariant = () => {
          const params = new URLSearchParams(window.location.search)
          const variantId = params.get('variant')
          if (variantId && variantId !== this._currentVariantId) {
            this._currentVariantId = variantId
            this._onVariantChange(variantId)
          }
        }

        if (!window.__sai_emblag01_history_patched__) {
          window.__sai_emblag01_history_patched__ = true
          const origPush = history.pushState.bind(history)
          const origReplace = history.replaceState.bind(history)
          window.__sai_emblag01_variant_handlers__ = []
          history.pushState = (...args) => {
            origPush(...args)
            for (const h of window.__sai_emblag01_variant_handlers__) h()
          }
          history.replaceState = (...args) => {
            origReplace(...args)
            for (const h of window.__sai_emblag01_variant_handlers__) h()
          }
          window.addEventListener('popstate', () => {
            for (const h of window.__sai_emblag01_variant_handlers__) h()
          })
        }
        window.__sai_emblag01_variant_handlers__.push(checkVariant)
        this._checkVariant = checkVariant
      }

      _teardownVariantObserver() {
        if (this._checkVariant && window.__sai_emblag01_variant_handlers__) {
          const idx = window.__sai_emblag01_variant_handlers__.indexOf(this._checkVariant)
          if (idx !== -1) window.__sai_emblag01_variant_handlers__.splice(idx, 1)
        }
      }

      _onVariantChange(variantId) {
        if (!this._pool) return
        const slot = resolveSlot(this._pool, variantId)
        const slotVariant = resolveSlotVariant(slot)
        if (!slotVariant?.assets) return
        const assets = resolveAssets(this._pool, slotVariant.assets)
        if (assets.length === 0) return
        this._allAssets = assets
        this._filterTags = buildFilterTags(slot?.filterTags)
        this._activeFilters = this._loadFilters()
        this._pendingFilters = {}
        this._applyFilters()
        this._renderFilterControls()
      }

      // ── Rebuild ──

      _rebuildGallery() {
        // The server-rendered gallery already reflects the resolved, unfiltered
        // asset set with a correctly prioritized eager LCP image. Rebuilding it
        // would discard that image and refetch a second one, delaying LCP — so
        // skip the rebuild while the live DOM still matches the asset set. The
        // active index resets only on the rebuild path — an adopted DOM keeps
        // showing whatever the shopper navigated to, so resetting the index
        // without a rebuild would desync state from the rendered image.
        if (this._domMatchesAssets(this._assets)) {
          // The SSR DOM is kept as-is (preserves the eager LCP image + the parked
          // offscreen slides); Embla just has to attach to the server-rendered
          // slides. _initEmbla runs only inside _rebuildSlides, which is skipped
          // here — so without this the carousel never initialises for the common
          // image-only gallery (no rebuild is ever triggered). Attach only when
          // not already running so a repeated _rebuildGallery with unchanged DOM
          // (double bootstrap, a filter/variant resolving to the same set)
          // doesn't tear down a live, mid-drag instance.
          if (!this._embla) this._initEmbla()
          this._restartAutoplay()
          this._builtAssetsKey = assetsKey(this._assets)
          return
        }
        // Redundant re-entry short-circuit. `_applyPool` runs twice on load (the
        // self-bootstrap DOMContentLoaded read AND bindAllContainers), and a
        // variant/filter can resolve to the set already on screen — so this can be
        // called again with the exact assets the track already renders. The
        // image-only fast-path above deliberately skips video galleries, so without
        // this guard a video gallery tears down its live Embla instance and rebuilds
        // identical slides on the second load pass. Gated on BOTH a completed client
        // build (`_builtAssetsKey`, unset until the first real rebuild finishes) and
        // a structural DOM match — NOT the structural match alone: the SSR DOM renders
        // video slides with matching data-index/data-handle, so `_renderedSlidesMatch`
        // is already true on the very first call and would wrongly skip the mandatory
        // first rebuild that wires video tracking + controls (JS the server can't
        // emit). The key is what says "already built these client-side"; a genuine
        // variant/filter change changes it, so that still rebuilds.
        if (
          this._builtAssetsKey === assetsKey(this._assets) &&
          this._renderedSlidesMatch(this._assets)
        ) {
          if (!this._embla) this._initEmbla()
          this._restartAutoplay()
          return
        }
        // The rebuild below replaces the track's slides in a single frame, so the
        // outgoing image disappears before the incoming one has decoded — a visible
        // flash on every variant/filter swap. Freeze the outgoing spotlight first
        // and fade it out once the replacement is ready.
        const frozen = this._freezeSpotlights()
        this._activeIndex = 0
        this._rebuildThumbs()
        this._rebuildSlides()
        this._rebuildIndicators()
        this._resumeActiveVideo()
        this._crossfadeFrom(frozen)
        this._builtAssetsKey = assetsKey(this._assets)
      }

      // Video-agnostic structural match: same slide count, same handles in order.
      // Unlike _domMatchesAssets this ignores active filters and video type — it
      // answers only "does the live track already render exactly these assets?",
      // used to skip a redundant rebuild when the resolved set is unchanged.
      _renderedSlidesMatch(assets) {
        return assets.length > 0 && this._slideDomMatches(assets)
      }

      // Same slide count and same data-index/data-handle in order — the shared spine
      // of _domMatchesAssets and _renderedSlidesMatch. How a slide's identity is
      // stamped must change in exactly one place, so both guards agree.
      _slideDomMatches(assets) {
        const slides = this.querySelectorAll('.sai-emblag01__slide')
        if (slides.length !== assets.length) return false
        for (let i = 0; i < assets.length; i++) {
          if (slides[i].dataset.index !== String(i)) return false
          if (slides[i].dataset.handle !== assets[i].handle) return false
        }
        return true
      }

      // Snapshot the currently painted spotlight image. Keyed on the ACTIVE slide,
      // not DOM order: every slide is in the track, so taking the first one would
      // clone slide 0 and flash an image the shopper isn't looking at whenever
      // they've navigated past the first slide. Read before the rebuild resets
      // `_activeIndex`, so this is still the outgoing selection.
      // Images only: a frozen <video> frame would fight the shared-video handoff in
      // _resumeActiveVideo, and video slides carry a poster that covers the swap.
      // Returns a list so _crossfadeFrom stays a plain loop — it is empty when the
      // gallery is off-screen or the active slide is a video.
      _freezeSpotlights() {
        const wrap = this.querySelector('.sai-emblag01__spotlight-wrap')
        if (!wrap?.offsetParent) return []
        const current = this._spotlightMediaAt(wrap, this._activeIndex)
        return current instanceof HTMLImageElement
          ? [{ wrap, clone: current.cloneNode(true) }]
          : []
      }

      // The spotlight media of a given slide index within the track's viewport.
      _spotlightMediaAt(wrap, index) {
        const slide = wrap.querySelector(`.sai-emblag01__slide[data-index="${index}"]`)
        return slide?.querySelector('.sai-emblag01__spotlight-media') ?? null
      }

      // Overlay each frozen snapshot over its (now rebuilt) viewport and fade it out
      // once the incoming image can paint. The overlay sits below the nav/zoom
      // controls (see the --xfade z-index) so they stay visible through the fade.
      _crossfadeFrom(frozen) {
        for (const { wrap, clone } of frozen) {
          clone.removeAttribute('loading')
          clone.classList.add('sai-emblag01__xfade')
          wrap.appendChild(clone)
          const incoming = this._spotlightMediaAt(wrap, this._activeIndex)
          const fadeOut = () => {
            requestAnimationFrame(() => clone.classList.add('is-out'))
            setTimeout(() => clone.remove(), XFADE_REMOVE_MS)
          }
          if (!(incoming instanceof HTMLImageElement) || (incoming.complete && incoming.naturalWidth)) {
            fadeOut()
            continue
          }
          incoming.addEventListener('load', fadeOut, { once: true })
          incoming.addEventListener('error', fadeOut, { once: true })
          // Failsafe only, for the case where neither load nor error ever fires
          // (detached element, aborted fetch) and the overlay would otherwise stick
          // over the gallery for good. Sized well clear of a slow-network fetch so
          // it doesn't cut a legitimate load short; if it ever does fire early the
          // shopper sees the pre-existing swap, never something worse.
          setTimeout(fadeOut, XFADE_LOAD_TIMEOUT_MS)
        }
      }

      // True only when the currently rendered slides are provably identical to
      // `assets` — same handles in the same contiguous positions, no active
      // filter, and no video. Any mismatch (filter, variant/experiment swap,
      // unresolved-handle index gap, or a video whose overlay controls and
      // analytics are wired only on rebuild) returns false so the caller
      // performs a full rebuild.
      _domMatchesAssets(assets) {
        if (Object.keys(this._activeFilters).length > 0) return false
        if (assets.length === 0) return false
        if (!assets.every((a) => a.type !== 'video')) return false
        return this._slideDomMatches(assets)
      }

      _rebuildThumbs() {
        const container = this.querySelector('.sai-emblag01__thumbs')
        if (!container) return
        const assets = this._assets
        // Update the strip in place: reuse each existing thumb box and crossfade
        // its image within the box (like the spotlight) rather than wiping the
        // whole strip and rebuilding it, which visibly blanks the rail and pops.
        // Boxes are only appended/removed when the asset count changes. The
        // crossfade is skipped when the strip isn't shown (offscreen / the mobile
        // dots-nav layout) or under reduced motion — the image then swaps instantly.
        const animate =
          !!container.offsetParent &&
          !(typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches)
        for (let i = 0; i < assets.length; i++) {
          const box = container.children[i]
          if (box) this._patchThumb(box, assets[i], i, animate)
          else container.appendChild(createThumbEl(assets[i], i, i === this._activeIndex))
        }
        while (container.children.length > assets.length) container.lastElementChild.remove()
      }

      // Update one existing thumb box in place — active state, the video play
      // affordance, and (when the image actually changed) crossfade the new image
      // in over the outgoing one within the box. `animate` false swaps instantly.
      _patchThumb(box, asset, index, animate) {
        box.dataset.index = String(index)
        box.setAttribute('aria-label', `View image ${index + 1}`)
        box.classList.toggle('sai-emblag01__thumb--active', index === this._activeIndex)
        const play = box.querySelector('.sai-emblag01__thumb-play')
        if (asset.type === 'video' && !play) box.appendChild(createThumbPlay())
        else if (asset.type !== 'video' && play) play.remove()
        if (box.dataset.handle === asset.handle) return
        box.dataset.handle = asset.handle

        const outgoing = box.querySelectorAll('.sai-emblag01__thumb-media')
        const img = createThumbMedia(asset)
        // `--enter` disables the transition so the overlay jumps to opacity 0;
        // releasing it lets the base transition run the real 0 -> 1 fade (the
        // always-on transition would otherwise animate *toward* 0 and never fade).
        if (animate) img.classList.add('sai-emblag01__thumb-media--overlay', 'sai-emblag01__thumb-media--enter')
        box.insertBefore(img, box.querySelector('.sai-emblag01__thumb-play'))
        if (!animate) {
          for (const o of outgoing) o.remove()
          return
        }
        requestAnimationFrame(() =>
          requestAnimationFrame(() => img.classList.remove('sai-emblag01__thumb-media--enter')),
        )
        let settled = false
        const settle = () => {
          if (settled) return
          settled = true
          for (const o of outgoing) o.remove()
          img.classList.remove('sai-emblag01__thumb-media--overlay')
        }
        img.addEventListener('transitionend', settle, { once: true })
        // Failsafe if the transition never fires (e.g. the tab is hidden before
        // the frame runs) so the outgoing image is never left stacked.
        setTimeout(settle, 600)
      }

      // Seam so tests can inject a fake carousel (real Embla needs layout jsdom
      // can't provide). A test may set `__SAI_EMBLA_FACTORY__` to drive the
      // integration; otherwise under the test harness we skip Embla entirely so
      // it never runs measurement-less. Production uses the shared engine handle
      // resolved by `_initEmbla` (window.__spectrumCarousel).
      _createEmbla(node, opts, Engine) {
        const factory = globalThis.__SAI_EMBLA_FACTORY__
        if (typeof factory === 'function') return factory(node, opts)
        if (globalThis.__SAI_TEST_HARNESS__) return null
        return Engine ? Engine(node, opts) : null
      }

      _teardownEmbla() {
        // Invalidate any in-flight async `_initEmbla`: it bails after its await if
        // this token moved (teardown, disconnect, or a newer rebuild) while the
        // shared engine was loading.
        this._emblaInitToken = (this._emblaInitToken || 0) + 1
        if (this._embla) {
          try {
            this._embla.destroy()
          } catch (_) {}
          this._embla = null
        }
      }

      // Initialise Embla on the one track. Embla owns every touch physics detail —
      // drag-follow, momentum, snapping, and the infinite loop — so there is no
      // hand-rolled scroll/clone/gesture code to drift or break. We only observe
      // its `select` event to keep our dots/thumbs/video/analytics in
      // sync, and drive it from the arrows/dots. Guarded: a single-slide gallery
      // or a measurement-less environment (jsdom) degrades to a static image.
      async _initEmbla() {
        this._teardownEmbla()
        const track = this.querySelector('.sai-emblag01__track')
        if (!track || this._assets.length <= 1) return
        // Load the shared engine (memoized across every carousel snippet on the
        // page, so N snippets trigger one fetch). Under the test harness the fake
        // factory drives `_createEmbla` synchronously, so skip the async load and
        // keep the existing synchronous tests valid.
        const token = this._emblaInitToken
        const Engine = globalThis.__SAI_TEST_HARNESS__ ? null : await ensureCarouselEngine()
        // A teardown / disconnect / newer rebuild during the await supersedes this
        // init — bail rather than attach a stale instance.
        if (token !== this._emblaInitToken || !this.isConnected) return
        try {
          this._embla = this._createEmbla(track, {
            loop: true,
            // Init on the current logical slide. A nav/dot tap before the shared
            // engine finished loading advances `_activeIndex` via the static
            // fallback; without this Embla would start at 0 and the track
            // would desync from the indicators/spotlight.
            startIndex: this._activeIndex,
            align: 'center',
            containScroll: false,
            skipSnaps: false,
            duration: 22,
            // Honour the `swipeEnabled` prop: false → keep arrows/dots/autoplay
            // (programmatic API) but disable finger-drag.
            watchDrag: this._props.swipeEnabled,
          }, Engine)
        } catch (_) {
          this._embla = null
        }
        if (!this._embla || typeof this._embla.on !== 'function') {
          this._embla = null
          return
        }
        this._onEmblaSelect = () => {
          const i = this._embla.selectedScrollSnap()
          if (i === this._activeIndex) return
          this._activeIndex = i
          this._updateDots()
          this._updateThumbs()
          // Pauses every non-active track video (incl. the one we just left) and
          // plays the new active one; the lightbox owns its own playback.
          this._syncActiveVideo()
          // A finger drag reaches here with the flag null (swipe); a programmatic
          // move (arrow/thumb/autoplay → 'nav', dot → 'dot') set it just before its
          // `scrollTo`, so the label carries through to this one analytics event.
          const source = this._programmaticNav || 'swipe'
          this._track(`${FEATURE_SLUG}:image_view_elem_clicked`, { index: i, source })
        }
        this._embla.on('select', this._onEmblaSelect)
        this._embla.on('reInit', this._onEmblaSelect)
        this._wireEmblaLazyLoad()
      }

      _rebuildIndicators() {
        const dotsContainer = this.querySelector('.sai-emblag01__dots')
        if (dotsContainer) {
          dotsContainer.innerHTML = ''
          for (let i = 0; i < this._assets.length; i++) {
            const dot = document.createElement('button')
            dot.className = `sai-emblag01__dot${i === this._activeIndex ? ' sai-emblag01__dot--active' : ''}`
            dot.type = 'button'
            dot.dataset.index = i
            dot.setAttribute('aria-label', `Go to image ${i + 1}`)
            dotsContainer.appendChild(dot)
          }
          // Fresh dot set (filter/variant change) — reset the scroll position so
          // the rebuilt strip starts from the left, then bring the active dot
          // into view (the loop above only marks it active).
          dotsContainer.scrollLeft = 0
          this._highlightDot(this._activeIndex)
        }
      }
    }
    customElements.define(TAG, SaiProductGalleryV2)
  }

  // ── Bind ──

  function bindAllContainers() {
    const snippetApi = window.__spectrumAi?.snippet
    const containers = document.querySelectorAll(
      `[data-spectrum-instance-id][data-spectrum-snippet-id="${SNIPPET_ID}"]`,
    )

    for (const node of containers) {
      const pool = readSnippetPool(node)
      const root = node.querySelector(TAG)
      if (!root) continue
      root._applyPool(pool)
      if (snippetApi) {
        const handles = snippetApi.bind(
          node,
          ({ variant, entry, pools, variants, currentVariantId }) => {
            const resolvedVariant =
              variant ?? variants?.find((v) => v.variantId === currentVariantId)
            if (!resolvedVariant?.content) return
            const snippetPool = readSnippetPool(node)
            root._applyPool(snippetPool)
          },
        )
        if (handles) root.setAnalytics(handles.track)
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bindAllContainers, { once: true })
  } else {
    bindAllContainers()
  }

  if (typeof globalThis !== 'undefined' && globalThis.__SAI_TEST_HARNESS__ === true) {
    globalThis.__saiIm4x2uig = {
      computeDotWindow,
      readSnippetPool,
      safeTrack,
      resolveSlot,
      resolveSlotVariant,
      resolveAssets,
      createSpotlightMedia,
      createThumbEl,
      filterAssets,
      buildFilterTags,
    }
  }
})()
